import React, { useState } from 'react';
import { toast } from 'sonner';

import { useSetAIBudget } from '@/hooks/useAIBudget';
import { ScanProfileModalFooter } from '@/sections/settings/components';
import { cn } from '@/utils/classname';

export interface AISpendLimitEditFormProps {
  email?: string;
  currentMaxLimit?: number;
  onClose?: () => void;
}

/**
 * Praetorian-only form for setting the per-tenant Bifrost monthly
 * spend cap. Single dollar input; backend pins reset_duration=1M and
 * calendar_aligned=true.
 */
export const AISpendLimitEditForm: React.FC<AISpendLimitEditFormProps> = ({
  email,
  currentMaxLimit,
  onClose,
}) => {
  const [value, setValue] = useState<string>(
    currentMaxLimit !== undefined ? String(currentMaxLimit) : ''
  );
  const [error, setError] = useState<string>('');

  const { mutateAsync: setBudget, status } = useSetAIBudget({ email });
  const isSaving = status === 'pending';

  const handleSave = async () => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setError('Enter a non-negative dollar amount.');
      return;
    }

    try {
      await setBudget({ maxLimit: parsed });
      onClose?.();
    } catch (e) {
      toast.error('Failed to update AI spend limit', {
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    }
  };

  return (
    <div className="flex flex-col gap-6" data-testid="ai-spend-limit-form">
      <div className="flex flex-col gap-2">
        <label
          htmlFor="ai-spend-max-limit"
          className="text-chariot-text-primary text-sm font-medium"
        >
          Monthly spend limit (USD)
        </label>
        <p className="text-chariot-text-secondary text-xs">
          Bifrost enforces this cap across every AI call attributed to this
          tenant. Resets on the first of each month (UTC).
        </p>
        <div className="flex items-center gap-2">
          <span className="text-chariot-text-secondary text-sm">$</span>
          <input
            id="ai-spend-max-limit"
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={value}
            onChange={e => {
              setValue(e.target.value);
              setError('');
            }}
            placeholder="100.00"
            aria-label="Monthly spend limit in dollars"
            className={cn(
              'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary',
              'flex-1 rounded-md border px-3 py-1.5 text-sm',
              'placeholder:text-chariot-text-secondary',
              'focus:ring-chariot-brand-primary focus:ring-1 focus:outline-hidden'
            )}
            data-testid="ai-spend-max-limit-input"
          />
        </div>
        {error && (
          <p className="text-xs text-red-500" data-testid="ai-spend-error">
            {error}
          </p>
        )}
      </div>

      <ScanProfileModalFooter
        primary={{
          label: 'Save',
          onClick: handleSave,
          disabled: isSaving || value === '',
          'data-testid': 'ai-spend-save-button',
        }}
        cancel={
          onClose
            ? {
                onClick: onClose,
                disabled: isSaving,
                'data-testid': 'ai-spend-cancel-button',
              }
            : undefined
        }
      />
    </div>
  );
};
